import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { useMasters } from '../../../contexts/MastersContext';
import { useBiddingResultGetListQuery, useBiddingConfirmCancelMutation } from '../../../hooks/useBiddingResultQuery';
import { BiddingItemStatusConst, CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../../constants';
import { BidConfirmationItemRow } from '../../../components/admin/BidConfirmation/BidConfirmationItemRow'; 
import { formatToDateInput, formatFromDateInput } from '../../../utils/date';
import { BiddingDisplayConditionConst } from '../../../constants/BiddingDisplayConditionConst';
import { ApprovalStatusConst } from '../../../constants';
import { useUpdateBidDisabledMutation } from '../../../hooks/useBiddingInfoQuery';
import { useBulkUpdateSubItemApprovalStatusMutation, useUpdateSubItemApprovalStatusMutation } from '../../../hooks/useBiddingItemsQuery';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { BiddingInputFilterDialog } from '../../../components/buyer/BiddingInput/BiddingInputFilterDialog';
import { useBiddingConfirmMutation } from '../../../hooks/useBiddingResultQuery';
import { useAppDialog } from '../../../hooks/useAppDialog';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { saveFilterPreference, getFilterPreference } from "../../../utils/filterStorage";
// import { useGetBiddingDateForResultQuery } from '../../../hooks/useCalendarQuery'; // 終了済の入札日取得用フック

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");
//--------------------------------
// 落札確定機能
//--------------------------------

export const BiddingConfirmationPage = () => {

  //----------------
  // Context
  //----------------
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading, sellers, farmers, teaTypes, teaSeasons, varieties, gaps } = useMasters();

  // // 終了済の入札日をAPIから取得
  // const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForResultQuery(currentUser?.loginId);

  //----------------
  // State / Hooks
  //----------------
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]); // 選択された行のキー
  const [approvalStatus, setApprovalStatus] = useState<string>("0"); // 0:未承認, 1:成立, 2:不成立
  const { showConfirm, OverlayElement, setIsOverlayActive } = useAppDialog();
  const [isInitialized, setIsInitialized] = useState(false);

  const pageTitle = "落札確定";

    /**
   * WebView2環境（デスクトップアプリのランタイム）かどうかを判定
   * ブラウザ等、専用アプリ外からの誤った印刷操作を抑制するために使用
   */
  const isWebView2 = !!(window.chrome && window.chrome.webview);

  //----------------
  // 初期値設定 (マウント時)
  //----------------
  // useEffect(() => {
  //   // 出品区分の初期値がなければ「通常入札(2)」をセット
  //   if (!itemFilters.listingCategory) {
  //       // 手合日がなければ当日などをセットする要件があればここに追加
  //       const today = new Date();
  //       // YYYY-MM-DD形式の文字列を作成
  //       const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        
  //       const preference = getFilterPreference()
  //       const defaultlistingCategory = preference.category; 
  //       const defaultPref = preference.prefecture;

  //       // 今回はユーザー入力待ちとする
  //       updateFiltersAndNavigate({ 
  //           ...itemFilters, 
  //           // listingCategory: ListingCategoryConst.BIDDING,
  //           listingCategory: defaultlistingCategory,
  //           // prefectureType: PrefTypeConst.INTERNAL,
  //           prefectureType: defaultPref,
  //           biddingDate: formatFromDateInput(dateStr),
  //           status: BiddingItemStatusConst.CONFIRMED,
  //           biddingDisplayCondition: BiddingDisplayConditionConst.BIDDING
  //       });
  //   }
  // }, []);
  //----------------
  // 初期値設定 (マウント時)
  //----------------
  useEffect(() => {
    // 必要なデータが揃うか、既に初期化済みならスキップ
    if (isMastersLoading || !currentUser || isInitialized) return;

    let hasChanged = false;
    const newFilters = { ...itemFilters };

    // 値がセットされていない場合のみ初期値をセットする
    if (!newFilters.listingCategory || !newFilters.prefectureType || !newFilters.biddingDate) {
      const preference = getFilterPreference();
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      if (!newFilters.listingCategory) {
        newFilters.listingCategory = preference?.category ?? ListingCategoryConst.BIDDING;
        hasChanged = true;
      }
      if (!newFilters.prefectureType) {
        newFilters.prefectureType = preference?.prefecture ?? PrefTypeConst.INTERNAL;
        hasChanged = true;
      }
      if (!newFilters.biddingDate) {
        newFilters.biddingDate = formatFromDateInput(dateStr);
        hasChanged = true;
      }
      // // 入札日の初期設定 (APIからの日付 or 今日)
      // if (!newFilters.biddingDate) {
      //   newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
      //   hasChanged = true;
      // }
      if (!newFilters.status) {
        newFilters.status = BiddingItemStatusConst.CONFIRMED;
        hasChanged = true;
      }
      if (!newFilters.biddingDisplayCondition) {
        newFilters.biddingDisplayCondition = BiddingDisplayConditionConst.BIDDING;
        hasChanged = true;
      }
    }

    if (hasChanged) {
      // 値を補完した場合はURLを更新し、一旦処理を抜ける（URL反映待ち）
      updateFiltersAndNavigate(newFilters);
      return; 
    }

    // URLにすべての値が完全に反映されたら、初期化完了フラグを立てる
    setIsInitialized(true);
  }, [isMastersLoading, currentUser, itemFilters, isInitialized, updateFiltersAndNavigate]);

  //----------------
  // Memo / Data Fetching
  //----------------
  const masterDataForComponents = useMemo(() => ({
      varieties, teaTypes, teaSeasons, sellers, farmers, gaps
  }), [varieties, teaTypes, teaSeasons, sellers, farmers, gaps]);

  // データ取得フック
  const {
    data: responseData,
    isLoading: isItemsLoading,
    isFetching,
    error,
  } = useBiddingResultGetListQuery(
      itemFilters, 
      currentUser?.loginId ?? ""
  );

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingResultConfirm'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  const items = useMemo(() => {
    if (!responseData || !responseData.items) return [];
    return responseData.items;
  }, [responseData]);

  const itemsIsConfirm = useMemo(() => {
    return responseData?.isConfirm;
  }, [responseData]);

  //----------------
  // Event Handlers
  //----------------
  
  // 入札日変更
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFiltersAndNavigate({ ...itemFilters, biddingDate: formatFromDateInput(e.target.value) });
  };

  //県内県外判定
  // 合体セレクトボックスの変更ハンドラー
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
    // 共通関数で数値として分解
    const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);

    // 選択値を保管
    saveFilterPreference(listingCategory, prefCategory);

    // ★ URL(Context)を更新。名前を prefectureType に統一
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: listingCategory,
      prefectureType: prefCategory, 
    });
  }, [itemFilters, updateFiltersAndNavigate]);

  // アコーディオン切替 (排他制御: 1つ開いたら他は閉じる)
  const handleToggleDetail = useCallback((uniqueKey: string) => {
    setExpandedKey(prev => (prev === uniqueKey ? null : uniqueKey));
  }, []);

  // 個別の選択切り替え
  const handleToggleSelect = useCallback((key: string) => {
    setSelectedKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  // 承認チェックボックス全選択・解除
  const handleSelectAll = useCallback(() => {
  if (!items || items.length === 0) return;    
  const allKeys = items.map(item => `${item.itemId}:${item.branchNumber}`);
  
  // 「表示されている全てが既に選択されているか」で判定
  const isAllSelected = allKeys.every(key => selectedKeys.includes(key));    
  if (isAllSelected) {
      // すべて解除
      setSelectedKeys([]);
  } else {
      // すべて選択
      setSelectedKeys(allKeys);
  }
  }, [items, selectedKeys]);

  const formatJapaneseDate = (dateString: string | undefined | null): string => {
  // 値が存在しない、または8桁の文字列でない場合はそのまま返す（フェイルセーフ）
  if (!dateString || dateString.length !== 8) {
    return dateString || "";
  }

  // 文字列から年・月・日を切り出す
  const year = dateString.substring(0, 4);
  
  // parseIntを使用することで、"03" のような先頭のゼロを取り除きます（"03" -> 3）
  const month = parseInt(dateString.substring(4, 6), 10);
  const day = parseInt(dateString.substring(6, 8), 10);

  // NaN (Not-a-Number) チェック: 数字以外の文字列が渡された場合の対策
  if (isNaN(month) || isNaN(day)) {
    return dateString;
  }

  return `${year}年${month}月${day}日`;
};

  const { mutate: updateSingleStatus } = useUpdateSubItemApprovalStatusMutation();
  const { mutate: bulkUpdateStatus } = useBulkUpdateSubItemApprovalStatusMutation(itemFilters, currentUser?.loginId ?? "");
  const { handleExecuteConfirm, isConfirm } = useBiddingConfirmMutation(itemFilters, currentUser?.loginId ?? "");
  const { handleExecuteConfirmCancel, isCancel } = useBiddingConfirmCancelMutation(itemFilters, currentUser?.loginId ?? "");

  const handleBulkApprove = () => {
    if (selectedKeys.length === 0) return;    
    const statusNum = Number(approvalStatus); 
    const statusLabel = 
      statusNum === ApprovalStatusConst.APPROVED ? "承認済（成立）" :
      statusNum === ApprovalStatusConst.DISAPPROVAL ? "承認済（不成立）" :
      "未承認";  
    
    showConfirm({
      title: pageTitle,
      message: `${selectedKeys.length}件を「${statusLabel}」として一括更新しますか？`,
      onConfirm: () => {
        const targets = selectedKeys.map(key => {
          const [itemId, branchNumber] = key.split(':');
          return { itemId, branchNumber: Number(branchNumber) };
        });
        bulkUpdateStatus({ targets, approvalStatus: statusNum, pageName: '落札確定機能' }, { onSuccess: () => setSelectedKeys([]) });
      }
    });
  };

  // 行コンポーネントに渡す個別更新用ハンドラ
  const handleSingleStatusChange = useCallback((itemId: string, branchNumber: number, nextStatus: number) => {
    updateSingleStatus({ itemId, branchNumber, approvalStatus: nextStatus });
  }, [updateSingleStatus]);

  // 入札無効化用のMutation
  const { mutate: updateBidDisabled } = useUpdateBidDisabledMutation();

  // 子から呼ばれるハンドラ
  const handleToggleBidDisabled = useCallback((itemId: string, branchNumber: number, buyerCode: number, isDisabled: boolean) => {
    updateBidDisabled({ itemId, branchNumber, buyerCode, isDisabled });
  }, [updateBidDisabled]);

  // 落札確定用ハンドラ
  const handleBiddingConfirm = useCallback(() => {
    const message = `${formatJapaneseDate(itemFilters.biddingDate)}の出品情報を落札確定してよろしいですか？`;
    // 【共通ダイアログ呼び出し】
    showConfirm({
      title: pageTitle,
      message: message,
      onConfirm: () => {
        const _prefectureType = itemFilters.prefectureType === 1 ? PrefTypeConst.INTERNAL : PrefTypeConst.EXTERNAL
        handleExecuteConfirm(items, _prefectureType);
      }
    });
  }, [items, itemFilters, showConfirm]);

  // 落札確定取消用ハンドラ
  const handleBiddingConfirmCancel = useCallback(() => {
    const message = `${formatJapaneseDate(itemFilters.biddingDate)}の落札確定取消してよろしいですか？`;
    // 【共通ダイアログ呼び出し】
    showConfirm({
      title: pageTitle,
      message: message,
      onConfirm: () => {
        handleExecuteConfirmCancel(itemFilters.biddingDate, itemFilters.listingCategory, itemFilters.prefectureType);
      }
    });
  }, [itemFilters, showConfirm]);

  //----------------
  // Render
  //----------------
  // const isPageLoading = isItemsLoading || isMastersLoading;
  const isPageLoading = isItemsLoading || isMastersLoading || !isInitialized;
  
  if (isPageLoading) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center">
        <p className="text-lg text-slate-500">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans relative">
      
      {/* 共通レイヤー */}
      {OverlayElement}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        <header className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-extrabold text-slate-800">{pageTitle}</h1>
          </div>

          {/* ヘッダー検索エリア (必須項目) */}
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex flex-col gap-2">
    
          {/* ━━━━━━━━━━━━━━━━━━━━━━━
            上段：入札日・出品区分（左） ＆ ボタン類（右）
            ※幅が広い時は1段にまとまり、狭い時は自動で2段に折り返す
            ━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div className="flex flex-wrap items-end justify-between gap-4 w-full">
              
              {/* ▼ 左側グループ：入札日、出品区分 ▼ */}
              <div className="flex flex-wrap items-start gap-6 w-full lg:w-auto">
                  {/* 1. 入札日 */}
                  <div className="w-full sm:w-auto">
                      <label className="block text-sm font-bold text-slate-700 mb-1">
                          入札日 
                      </label>
                      <input 
                          type="date" 
                          value={formatToDateInput(String(itemFilters.biddingDate))} 
                          onChange={handleDateChange}
                          className="w-full sm:w-48 p-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none h-[42px] safari-date-fix"
                      />
                  </div>
                    
                  {/* 2. 出品区分 */}
                  <div className="w-full sm:w-auto">
                      <label className="block text-sm font-bold text-slate-700 mb-1">
                          出品区分（県内/県外）
                      </label>
                      <select
                          value={
                              (itemFilters.listingCategory !== null && itemFilters.prefectureType != null)
                              ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                              : ""
                          } 
                          onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                          className="w-full sm:w-56 p-2 border border-slate-300 rounded-md bg-white"
                      >
                          {CombinedCategoryOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                      </select>
                  </div>
              </div>

                {/* ▼ 右側グループ：落札確定・取消ボタン ▼ */}
                {/* 折り返して2段になった時も右寄せになるように justify-end などを維持 */}
                <div className="flex flex-wrap items-center justify-end gap-3 w-full lg:w-auto ml-auto">
                    {!isWebView2 && (
                        <span className="text-base text-red-500 font-medium whitespace-nowrap">
                            ※専用アプリからのみ落札確定可能です
                        </span>
                    )}
                    
                    {/* 落札確定ボタン */}
                    <button 
                        onClick={handleBiddingConfirm}
                        disabled={!isWebView2 || !items || items.length == 0 || isConfirm || itemsIsConfirm}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-md text-sm font-bold transition-all shadow-sm border ${
                            items && items.length > 0 && isWebView2 && !itemsIsConfirm
                            ? 'bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-700' 
                            : 'bg-slate-200 border-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                    >
                        {isConfirm ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                確定処理中...
                            </>
                        ) : "落　札　確　定"}
                    </button>

                    {/* 落札取消ボタン */}
                    <button 
                        onClick={handleBiddingConfirmCancel}
                        disabled={!isWebView2 || !items || items.length == 0 || isCancel || !itemsIsConfirm}
                        className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 rounded-md text-sm font-bold transition-all shadow-sm border ${
                            items && items.length > 0 && isWebView2 && itemsIsConfirm
                            ? 'bg-red-600 border-red-700 text-white hover:bg-red-700' 
                            : 'bg-slate-200 border-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                    >
                        {isCancel ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                取消処理中...
                            </>
                        ) : "落　札　取　消"}
                    </button>
                </div>
            </div>
            
            {/* ━━━━━━━━━━━━━━━━━━━━━━━
                下段（旧3段目）：メッセージ（左寄せ） / その他（右寄せ） 
                ━━━━━━━━━━━━━━━━━━━━━━━ */}
            <div className="flex flex-wrap items-center gap-3 w-full mt-2">
                {/* メッセージ（左側） */}
                {itemsIsConfirm && (
                    <div className="text-base text-red-500 font-medium whitespace-nowrap">
                        ※対象日は落札確定済みです。
                    </div>
                )}
                
                {/* すべて選択（ml-autoでここから右側に押しやる） */}
                <div className="flex items-center gap-2 px-2 ml-auto">
                    <input 
                        type="checkbox"
                        checked={!!items && items.length > 0 && selectedKeys.length === items.length}
                        onChange={handleSelectAll}
                        className="w-5 h-5 accent-indigo-600 cursor-pointer rounded border-slate-300"
                    />
                    <label className="text-sm font-bold text-slate-700 cursor-pointer whitespace-nowrap">
                        すべて選択 <span className="text-xs font-normal text-slate-500">({items?.length ?? 0}件)</span>
                    </label>
                </div>
                
                {/* 承認状態セレクト */}
                <div className="flex-1 sm:flex-none">
                    <select 
                        value={approvalStatus}
                        onChange={(e) => setApprovalStatus(e.target.value)} 
                        className="w-full sm:w-48 p-2 border border-slate-300 rounded-md bg-white text-sm focus:ring-indigo-500"
                    >
                        <option value={ApprovalStatusConst.UNAPPROVED}>未承認</option>
                        <option value={ApprovalStatusConst.APPROVED}>承認済（成立）</option>
                        <option value={ApprovalStatusConst.DISAPPROVAL}>承認済（不成立）</option>
                    </select>
                </div>
                
                {/* 更新ボタン */}
                <button 
                    disabled={selectedKeys.length === 0}
                    onClick={handleBulkApprove}
                    className={`w-full sm:w-auto px-6 py-2 rounded-md text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                        selectedKeys.length > 0 
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                >
                    更新
                </button>
            </div>
          </div>
        </header>

        <main>
            {isPageLoading && <div className="text-center py-10">読み込み中...</div>}
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    エラーが発生しました: {error.message}
                </div>
            )}

            {!isPageLoading && !error && (
                <>
                    { (responseData && !responseData.isSuccess) ? (
                      <div className="text-center py-10 text-gray-500">
                        {responseData.message}
                      </div>
                    ) : (!items || items.length === 0) ? (
                        <div className="text-center py-20 text-gray-500">
                            該当する取引情報がありません。
                        </div>
                    ) : (
                        
                        /* 一覧表示 */
                        <div className="space-y-4">
                          {items.map(item => {
                              // 一意なキーを生成
                              const uniqueKey = `${item.itemId}:${item.branchNumber}`;                 
                              return (
                                  <BidConfirmationItemRow 
                                      key={uniqueKey}
                                      item={item} 
                                      isExpanded={expandedKey === uniqueKey}
                                      onToggle={() => handleToggleDetail(uniqueKey)}
                                      isChecked={selectedKeys.includes(uniqueKey)}
                                      onCheck={() => handleToggleSelect(uniqueKey)}
                                      onToggleBidDisabled={handleToggleBidDisabled}
                                      onStatusChange={handleSingleStatusChange}
                                      setIsOverlayActive={setIsOverlayActive}
                                  />
                              );
                          })}
                        </div>
                    )}
                </>
            )}
        </main>

        {/* 検索ダイアログ (共通コンポーネント) */}
        <BiddingInputFilterDialog 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)} 
          masterData={masterDataForComponents}
          isSeller = {false}
          isFarmer = {false}
        /> 
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching || isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isFetching ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};